import type { FilterCondition, FilterGroup, GroupLogic } from './filter';
import { normalizeGroupBy, type FlowSort } from './view';

/**
 * Canonical view-definition normalization — ONE function for the client's
 * dirty diff and the server's save serialization (U6).
 */

/**
 * The dirty-relevant subset of a view's state, as loosely as its three
 * producers actually spell it: a `flow_views` row (`group_logic` NOT NULL
 * with a DB default, `group_by` nullable), a `FlowView` preset (`groupBy`/
 * `groupLogic` optional/absent — presets have no stored combinator), and the
 * client's live working state (`groupBy: undefined`). Normalizing away those
 * spelling differences IS the job — so the input type tolerates them rather
 * than forcing every caller to pre-canonicalize (which is exactly the
 * per-caller drift this module exists to prevent).
 */
export interface ViewDefinitionInput {
  filter?: FilterGroup[] | null;
  groupLogic?: GroupLogic | null;
  sort: FlowSort;
  /** Every legacy shape `normalizeGroupBy` accepts — a single string (bare or
   *  comma-joined), an array (deduped on normalize), or absent/null. */
  groupBy?: string[] | string | null;
  columns?: string[] | null;
}

export interface NormalizedViewDefinition {
  filter: FilterGroup[];
  groupLogic: GroupLogic;
  sort: FlowSort;
  /** Ordered, deduped levels — `[]` means "no grouping" (R5). */
  groupBy: string[];
  columns: string[];
}

function normalizeCondition(condition: FilterCondition): FilterCondition {
  return {
    id: condition.id,
    field: condition.field,
    operator: condition.operator,
    value: condition.value,
  };
}

function normalizeGroup(group: FilterGroup): FilterGroup {
  return {
    id: group.id,
    logic: group.logic,
    conditions: group.conditions.map(normalizeCondition),
    // QA round 8 Codex fixes, F6 — `boxed` is display-only (like `label`)
    // but it is the rep's saved intent for how the group is drawn; dropping
    // it here flattened every saved lone "Filter group" on reopen.
    // Conditional spread, never `boxed: group.boxed`: an `undefined` key
    // must not land in the stored JSON or in a `toEqual` against a
    // pre-round-8 group.
    ...(group.boxed === true ? { boxed: true } : {}),
  };
}

export function normalizeViewDefinition(input: ViewDefinitionInput): NormalizedViewDefinition {
  return {
    // A conditionless group constrains nothing — "+ Add filter group" leaves
    // one behind in the UI, and it must never read as an unsaved edit.
    filter: (input.filter ?? []).filter(g => g.conditions.length > 0).map(normalizeGroup),
    // Presets have no `groupLogic` key (they have no stored combinator to
    // carry) and an absent/legacy value on any other producer means the
    // same default, AND. Without this a freshly opened preset would read as
    // dirty the moment the client seeded its working state with the literal
    // 'AND'.
    groupLogic: input.groupLogic ?? 'AND',
    sort: { field: input.sort.field, dir: input.sort.dir },
    groupBy: normalizeGroupBy(input.groupBy),
    columns: [...(input.columns ?? [])],
  };
}

/**
 * Stable string key for EQUALITY comparison — the normalized definition
 * projected down to what a rep can actually see in the table:
 *
 *  - group/condition `id`s are dropped. They are React keys and
 *    role-default/React-key stability handles, not predicate content; the same
 *    predicate rebuilt by the popover carries fresh random ids, and comparing
 *    on them is the single largest source of phantom "Modified" banners.
 *  - array values compare as SETS. `is_any_of ['a','b']` and
 *    `is_any_of ['b','a']` select identical rows, so a multi-select whose
 *    chips got reordered is not an edit. (`normalizeViewDefinition` keeps the
 *    author's order for storage — only the comparison key sorts.)
 *  - `boxed` (and `label`) are display flags, excluded here on purpose: how
 *    a group is DRAWN (bracketed card vs. flat) is not part of what rows it
 *    selects, so toggling it must never make a saved view read as modified
 *    (QA round 8 Codex fixes, F6).
 *
 * Everything else — column order included (R4: reorder marks dirty) — is
 * compared verbatim.
 */
export function viewDefinitionKey(input: ViewDefinitionInput): string {
  const normalized = normalizeViewDefinition(input);
  return JSON.stringify({
    filter: normalized.filter.map(group => ({
      logic: group.logic,
      conditions: group.conditions.map(condition => ({
        field: condition.field,
        operator: condition.operator,
        value: Array.isArray(condition.value) ? [...condition.value].sort() : condition.value,
      })),
    })),
    groupLogic: normalized.groupLogic,
    sort: normalized.sort,
    groupBy: normalized.groupBy,
    columns: normalized.columns,
  });
}

export function viewDefinitionsEqual(a: ViewDefinitionInput, b: ViewDefinitionInput): boolean {
  return viewDefinitionKey(a) === viewDefinitionKey(b);
}
